import React from "react";
import { useFormContext } from "react-hook-form";
import MUploadFile from "@app/components/MUploadFile";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { useFileParser } from "@app/pages/samples/hook/useFileParser";
import { cleanFileName } from "@app/pages/samples/utils/calculationHelpers";

export const parseRptFileGenie = (content, isotope) => {
  const lines = content.split("\n");

  let data: any = {};
  let equipmentFound = false;
  let detectorFound = false;
  let dateCountingFound = false;
  let timeCountingFound = false;
  let volumenCountingFound = false;
  let certificateTrazadorFound = false;
  let fondoFound = false;
  let calibrationFound = false;
  let libraryFound = false;
  let sampleFound = false;

  let totalActivity = 0;
  let totalUncertainty = 0;
  let totalLid = 0;

  let isReadingNuclides = false;
  let isReportNuclides = false;
  let isSesionVolumen = false;
  let isSesionEficienciaTotal = false;

  lines.forEach((line) => {
    if (
      line.includes(
        "*****   I N T E R F E R E N C E   C O R R E C T E D   R E P O R T   *****"
      )
    ) {
      isReadingNuclides = true;
      return;
    }

    if (
      line.includes("       ? = nuclide is part of an undetermined solution")
    ) {
      isReadingNuclides = false;
      return;
    }

    if (isReadingNuclides) {
      const parts = line.trim().split(/\s+/);
      const formattedIsotope = isotope.replace(/(\d+)/, "-$1").toUpperCase();

      if (parts[0] === isotope || parts[0] === formattedIsotope || (formattedIsotope == "CM-243" && parts[0] == "CM-244")) {
        const activity = parseFloat(parts[2]);
        const uncertainty = parseFloat(parts[3]);

        if (!isNaN(activity)) totalActivity = activity;
        if (!isNaN(uncertainty)) totalUncertainty = uncertainty;
      }

      data.totalActivity = totalActivity;
      data.totalUncertainty = totalUncertainty;
    }

    // if (line.includes("-----        NUCLIDE ACTIVITY REPORT         -----")) {
    //   isReportNuclides = true;
    //   return;
    // }

    if (line.includes("-----        NUCLIDE MDA REPORT        ----")) {
      isReportNuclides = true;
      return;
    }

    if (isReportNuclides) {
      const parts = line.trim().split(/\s+/);
      const formattedIsotope = isotope.replace(/(\d+)/, "-$1").toUpperCase();

      if (parts[1] === isotope || parts[1] === formattedIsotope || (formattedIsotope == "CM-243" && parts[1] == "CM-244")) {
        const lid = parseFloat(parts[5]);

        if (!isNaN(lid)) totalLid = lid;
      }

      data.totalLid = totalLid;
    }

    if (isSesionVolumen) {
      const parts = line.trim().split(/\s+/);
      data.volume = parts[2];
      isSesionEficienciaTotal = true;
      isSesionVolumen = false;
      return;
    }

    if (isSesionEficienciaTotal) {
      const parts = line.trim().split(/\s+/);
      data.efficiencieTotal = parseFloat(parts[2]);
      isSesionEficienciaTotal = false;
    }

    if (line.includes("FECHA DE CONTAJE") && !dateCountingFound) {
      data.date_counting = `${line.split(":")[1].trim()}:${line
        .split(":")[2]
        .trim()}:${line.split(":")[3].trim()}`;

      dateCountingFound = true;
    }

    if (line.includes("DETECTOR") && !equipmentFound) {
      const equipment = line.split(":")[1].trim();
      data.equipment = equipment.charAt(0);
      equipmentFound = true;
    }

    if (line.includes("DETECTOR") && !detectorFound) {
      const equipment = line.split(":")[1].trim();
      data.detector = equipment.slice(-2);
      detectorFound = true;
    }

    if (line.includes("TIEMPO DE CONTAJE") && !timeCountingFound) {
      data.time_count = line.split(":")[1].trim().replace(/\D/g, "");
      timeCountingFound = true;
    }

    if (line.includes("EFICIENCIA ") && !volumenCountingFound) {
      data.volume = line.split(":")[1].trim();
      volumenCountingFound = true;
    }

    if (
      line.includes("ARCHIVO CERTIFICADO TRAZADOR") &&
      !certificateTrazadorFound
    ) {
      data.certificateTrazador = line.split(": ")[1].trim().split(/[/\\]/).pop();
      volumenCountingFound = true;
      isSesionVolumen = true;
      return;
    }

    if (line.includes("FONDO") && !fondoFound) {
      data.fondo = line.split(": ")[1].trim().split(/[/\\]/).pop();
      fondoFound = true;
    }

    if (
      (line.includes("CALIBRAC") || line.includes("CALIBRACIÃ“N:")) &&
      !calibrationFound
    ) {
      data.calibration = line.split(": ")[1].trim();
      calibrationFound = true;
    }

    if (line.includes("Nuclide Library Used") && !libraryFound) {
      data.library = line.split(": ")[1].trim().split(/[/\\]/).pop();
      libraryFound = true;
    }

    if (line.includes("MUESTRA") && !sampleFound) {
      data.sample = line.split(": ")[1].trim();
      sampleFound = true;
    }
  });

  return data;
};

interface FileUploadGenieProps {
  onDataParsed?: (data: {
    totalActivity?: number;
    totalUncertainty?: number;
    totalLid?: number;
    efficiencieTotal?: number;
    counting_detector?: any[];
  }) => void;
}

const FileUploadGenie: React.FC<FileUploadGenieProps> = ({ onDataParsed }) => {
  const { setValue, getValues, watch } = useFormContext();
  const { currentCalculation, importFileCalculation } = useAnalysis();
  const { parseAndMapData, mapDataToFormFields } = useFileParser();

  const parseRptFile = (content: string) => {
    if (!content) return;

    const isotope = getValues("isotope_code");
    const parsedData = parseRptFileGenie(content, isotope);
    const mappedData = parseAndMapData(parsedData);
    
    const formFields = mapDataToFormFields(
      parsedData, 
      mappedData.equipment, 
      mappedData.detector
    );
    
    // Notificar al componente padre sobre los datos parseados
    if (onDataParsed) {
      onDataParsed({
        totalActivity: parsedData.totalActivity,
        totalUncertainty: parsedData.totalUncertainty,
        totalLid: parsedData.totalLid,
        efficiencieTotal: parsedData.efficiencieTotal,
        counting_detector: mappedData.detector ? [mappedData.detector] : [],
      });
    }
    
    // Aplicar todos los valores al formulario
    Object.entries(formFields).forEach(([key, value]) => {
      setValue(key, value);
    });
  };

  const handleDownload = async () => {
    await importFileCalculation(
      getValues("source_app_calculation_info_file"),
      "DOWNLOAD"
    );
  };

  const isDisabled = Boolean(watch("idResultPreload"));

  return (
    <MUploadFile
      key={`${getValues("source_app_calculation_info_file")}_genie`}
      acceptFile=".rpt"
      onFileChange={parseRptFile}
      name="source_app_calculation_info_file_genie"
      preloadNameFile={cleanFileName(
        getValues("source_app_calculation_info_file")
      )}
      validation={
        currentCalculation.id ? {} : { required: "Este campo es requerido" }
      }
      downloadFile={currentCalculation.id ? handleDownload : undefined}
      disabled={isDisabled}
    />
  );
};
export default FileUploadGenie;